import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Slide,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { 
  Close as CloseIcon, 
  CheckOutlined,
  EmailOutlined,
  PhoneOutlined,
  CelebrationOutlined
} from '@mui/icons-material';

interface OptInResult {
  success: boolean;
  isDuplicate?: boolean;
  verified?: boolean;
  promoCode?: string;
  message?: string;
  status?: string;
}

interface OptInModalProps {
  open: boolean;
  onClose: () => void;
  onSendCode: (data: { email?: string; phoneNumber?: string }) => Promise<OptInResult>;
  onVerifyCode: (data: { email?: string; phoneNumber?: string; code: string }) => Promise<OptInResult>;
  restaurantName: string;
  restaurantLogoUrl?: string;
  promotionalMessage: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Email validation regex
const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;

// Phone validation regex (US format, flexible)
const phoneRegex = /^(\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

const OptInModal: React.FC<OptInModalProps> = ({
  open,
  onClose,
  onSendCode,
  onVerifyCode,
  restaurantName,
  restaurantLogoUrl,
  promotionalMessage
}) => {
  const [step, setStep] = useState<1 | 2>(1); // 1 = enter email/phone, 2 = enter code
  const [inputType, setInputType] = useState<'email' | 'phone'>('phone');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Reset all states when modal opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setInputType('phone');
      setEmail('');
      setPhone('');
      setVerificationCode('');
      setConsentChecked(false);
      setIsSubmitting(false);
      setError(null);
      setShowSuccess(false);
      setIsDuplicate(false);
    }
  }, [open]);

  const validateInput = (): boolean => {
    if (inputType === 'email') {
      if (!email.trim()) {
        setError('Please enter your email address');
        return false;
      }
      if (!emailRegex.test(email.trim())) {
        setError('Please enter a valid email address');
        return false;
      }
    } else {
      if (!phone.trim()) {
        setError('Please enter your phone number');
        return false;
      }
      if (!phoneRegex.test(phone.trim().replace(/\s/g, ''))) {
        setError('Please enter a valid phone number');
        return false;
      }
    }
    
    if (!consentChecked) {
      setError('Please agree to receive promotional messages');
      return false;
    }
    
    return true;
  };

  // Step 1: Send verification code
  const handleSendCode = async () => {
    if (!validateInput()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await onSendCode({
        email: inputType === 'email' ? email.trim() : undefined,
        phoneNumber: inputType === 'phone' ? phone.trim() : undefined
      });
      
      setIsSubmitting(false);
      
      // Check if already a member
      if (result?.isDuplicate) {
        setIsDuplicate(true);
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setIsDuplicate(false);
          onClose();
        }, 2500);
        return;
      }
      
      // Success - move to step 2
      setStep(2);
      
    } catch (err: any) {
      setError(err?.message || 'Unable to send verification code. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Step 2: Verify code and get promo
  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    if (verificationCode.trim().length < 4) {
      setError('Please enter a valid verification code');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await onVerifyCode({
        email: inputType === 'email' ? email.trim() : undefined,
        phoneNumber: inputType === 'phone' ? phone.trim() : undefined,
        code: verificationCode.trim()
      });
      
      setIsSubmitting(false);
      
      if (result?.verified || result?.success) {
        setShowSuccess(true);
        // Close modal after showing success
        setTimeout(() => {
          setShowSuccess(false);
          onClose();
        }, 2500);
      } else {
        setError(result?.message || 'Invalid verification code');
      }
      
    } catch (err: any) {
      setError(err?.message || 'Invalid or expired code. Please try again.');
      setIsSubmitting(false);
    }
  };

  // Handle back button (from step 2 to step 1)
  const handleBack = () => {
    setStep(1);
    setVerificationCode('');
    setError(null);
  };

  // Resend code
  const handleResendCode = async () => {
    setError(null);
    setIsSubmitting(true);
    
    try {
      await onSendCode({
        email: inputType === 'email' ? email.trim() : undefined,
        phoneNumber: inputType === 'phone' ? phone.trim() : undefined
      });
      setIsSubmitting(false);
      setError(null);
      // Show brief confirmation
      setError('New code sent!');
      setTimeout(() => setError(null), 2000);
    } catch (err: any) {
      setError(err?.message || 'Unable to resend code. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !showSuccess) {
      onClose();
    }
  };

  const handleInputTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newType: 'email' | 'phone' | null
  ) => {
    if (newType !== null) {
      setInputType(newType);
      setError(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          margin: 2,
          maxHeight: 'calc(100vh - 32px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Paper elevation={0} sx={{ borderRadius: 3 }}>
          {showSuccess ? (
            // Success State
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: isDuplicate ? '#0891B2' : '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                }}
              >
                <CelebrationOutlined sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                {isDuplicate ? 'Welcome back!' : "You're all set!"}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isDuplicate ? (
                  "You're already a VIP member!"
                ) : (
                  <>
                    Your promo code is now in your chat.
                    <br />
                    Show it to your server to redeem!
                  </>
                )}
              </Typography>
              
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  px: 4,
                  py: 1.5,
                  backgroundColor: '#0891B2',
                  '&:hover': {
                    backgroundColor: '#0e7490',
                  }
                }}
              >
                Back to Chat
              </Button>
            </Box>
          ) : (
            <>
              {/* Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #0891B2 0%, #0e7490 100%)',
                  color: 'white',
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {restaurantLogoUrl ? (
                    <Box
                      component="img"
                      src={restaurantLogoUrl}
                      alt={restaurantName}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        objectFit: 'cover',
                        backgroundColor: 'white'
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CelebrationOutlined sx={{ fontSize: 28 }} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Join Our VIP List
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {restaurantName}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleClose}
                  disabled={isSubmitting}
                  sx={{ 
                    color: 'white',
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.1)' 
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Content */}
              <Box sx={{ p: 3 }}>
                {step === 1 ? (
                  <>
                    {/* Step 1: Enter Email/Phone */}
                    
                    {/* Input Type Toggle */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        How would you like to be contacted?
                      </Typography>
                      <ToggleButtonGroup
                        value={inputType}
                        exclusive
                        onChange={handleInputTypeChange}
                        fullWidth
                        sx={{
                          '& .MuiToggleButton-root': {
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            '&.Mui-selected': {
                              backgroundColor: '#0891B2',
                              color: 'white',
                              '&:hover': {
                                backgroundColor: '#0e7490',
                              }
                            }
                          }
                        }}
                      >
                        <ToggleButton value="email">
                          <EmailOutlined sx={{ mr: 1 }} />
                          Email
                        </ToggleButton>
                        <ToggleButton value="phone">
                          <PhoneOutlined sx={{ mr: 1 }} />
                          Phone
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Input Field */}
                    <Box sx={{ mb: 3 }}>
                      {inputType === 'email' ? (
                        <TextField
                          fullWidth
                          type="email"
                          label="Email Address"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setError(null);
                          }}
                          placeholder="your@email.com"
                          variant="outlined"
                          disabled={isSubmitting}
                          autoComplete="email"
                          InputProps={{
                            startAdornment: (
                              <EmailOutlined sx={{ mr: 1, color: 'action.active' }} />
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          type="tel"
                          label="Phone Number"
                          value={phone}
                          onChange={(e) => {
                            setPhone(e.target.value);
                            setError(null);
                          }}
                          placeholder="(555) 123-4567"
                          variant="outlined"
                          disabled={isSubmitting}
                          autoComplete="tel"
                          InputProps={{
                            startAdornment: (
                              <PhoneOutlined sx={{ mr: 1, color: 'action.active' }} />
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      )}
                    </Box>

                    {/* Consent Checkbox */}
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={consentChecked}
                            onChange={(e) => {
                              setConsentChecked(e.target.checked);
                              setError(null);
                            }}
                            disabled={isSubmitting}
                            sx={{
                              '&.Mui-checked': {
                                color: '#0891B2',
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                            By checking this box and providing my phone number and/or email, I agree to receive
                            promotional and marketing messages from {restaurantName}, including offers, rewards,
                            and event information. Messages may be sent by {restaurantName}, TableChat acting on
                            its behalf, or authorized service providers using automated technology. Consent is
                            not required as a condition of purchase. Message frequency varies. Message and data
                            rates may apply. Reply STOP to unsubscribe or HELP for help. View{' '}
                            <Link 
                              href="https://tablechat.io/messaging-policy" 
                              target="_blank"
                              sx={{ color: '#0891B2' }}
                            >
                              Messaging Policy
                            </Link>
                            {' and '}
                            <Link 
                              href="https://tablechat.io/privacy" 
                              target="_blank"
                              sx={{ color: '#0891B2' }}
                            >
                              Privacy Policy
                            </Link>
                          </Typography>
                        }
                      />
                    </Box>

                    {/* Error Message */}
                    {error && (
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="body2" 
                          color={error === 'New code sent!' ? 'success.main' : 'error'}
                          sx={{ 
                            backgroundColor: error === 'New code sent!' ? '#e8f5e9' : '#ffebee',
                            p: 2, 
                            borderRadius: 1,
                            border: error === 'New code sent!' ? '1px solid #a5d6a7' : '1px solid #ffcdd2'
                          }}
                        >
                          {error}
                        </Typography>
                      </Box>
                    )}

                    {/* Send Code Button */}
                    <Button
                      fullWidth
                      onClick={handleSendCode}
                      disabled={isSubmitting}
                      variant="contained"
                      size="large"
                      startIcon={
                        isSubmitting ? <CircularProgress size={20} color="inherit" /> : 
                        <CheckOutlined />
                      }
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        backgroundColor: '#0891B2',
                        '&:hover': {
                          backgroundColor: '#0e7490',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#b0bec5',
                        }
                      }}
                    >
                      {isSubmitting ? 'Sending Code...' : 'Send Verification Code'}
                    </Button>

                    {/* Skip Link */}
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Maybe later
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    {/* Step 2: Enter Verification Code */}
                    
                    {/* Info Message */}
                    <Box
                      sx={{
                        backgroundColor: '#e0f2f1',
                        borderRadius: 2,
                        p: 2,
                        mb: 3,
                        border: '1px solid #80cbc4'
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#00695c' }}>
                        📱 We sent a verification code to:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#00695c', mt: 0.5, fontWeight: 600 }}>
                        {inputType === 'email' ? email : phone}
                      </Typography>
                      {inputType === 'email' && (
                        <Typography variant="caption" sx={{ color: '#00796b', mt: 1, display: 'block' }}>
                          Not in your inbox? Check spam and mark as "not spam".
                        </Typography>
                      )}
                    </Box>

                    {/* Verification Code Input */}
                    <Box sx={{ mb: 3 }}>
                      <TextField
                        fullWidth
                        type="text"
                        label="Verification Code"
                        value={verificationCode}
                        onChange={(e) => {
                          // Only allow numbers (4 digits)
                          const value = e.target.value.replace(/\D/g, '').substring(0, 4);
                          setVerificationCode(value);
                          setError(null);
                        }}
                        placeholder=""
                        variant="outlined"
                        disabled={isSubmitting}
                        autoComplete="one-time-code"
                        inputProps={{
                          maxLength: 4,
                          style: { 
                            letterSpacing: '0.75em', 
                            textAlign: 'center',
                            fontSize: '1.75rem',
                            fontWeight: 600
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    </Box>

                    {/* Error Message */}
                    {error && (
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="body2" 
                          color={error === 'New code sent!' ? 'success.main' : 'error'}
                          sx={{ 
                            backgroundColor: error === 'New code sent!' ? '#e8f5e9' : '#ffebee',
                            p: 2, 
                            borderRadius: 1,
                            border: error === 'New code sent!' ? '1px solid #a5d6a7' : '1px solid #ffcdd2'
                          }}
                        >
                          {error}
                        </Typography>
                      </Box>
                    )}

                    {/* Verify Button */}
                    <Button
                      fullWidth
                      onClick={handleVerifyCode}
                      disabled={isSubmitting || verificationCode.length < 4}
                      variant="contained"
                      size="large"
                      startIcon={
                        isSubmitting ? <CircularProgress size={20} color="inherit" /> : 
                        <CheckOutlined />
                      }
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600,
                        backgroundColor: '#0891B2',
                        '&:hover': {
                          backgroundColor: '#0e7490',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#b0bec5',
                        }
                      }}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify & Get Promo Code'}
                    </Button>

                    {/* Resend / Back Links */}
                    <Box sx={{ textAlign: 'center', mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button
                        onClick={handleBack}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        ← Change {inputType}
                      </Button>
                      <Button
                        onClick={handleResendCode}
                        disabled={isSubmitting}
                        sx={{
                          textTransform: 'none',
                          color: '#0891B2',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                      >
                        Resend Code
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default OptInModal;
